import {
  ArrowRight,
  PenTool,
  Share2,
  Brain,
  LayoutGrid,
  Grid2x2,
  Columns3,
  Layers,
  Timer,
  Compass,
  Cpu,
  NotebookPen,
  FileCode2,
  FileText,
  FileType,
  ScanText,
  Type,
  Stethoscope,
  AudioLines,
  LayoutPanelTop,
  Workflow,
  HeartHandshake,
  FileJson,
  GitFork,
  CornerDownRight,
  LayoutPanelLeft,
  Milestone,
  Blend,
  Camera,
  Tags,
  SquareKanban,
  Network,
  Group,
  Scissors,
  type LucideIcon,
} from "lucide-react";
import { useMetaTags } from "../components/MetaTags";
import { tools } from "./tools";
import { withToolAttribution } from "../lib/toolAttribution";

const ICONS: Record<string, LucideIcon> = {
  Scissors,
  Group,
  Tags,
  SquareKanban,
  Network,
  AudioLines,
  LayoutPanelTop,
  Workflow,
  HeartHandshake,
  FileJson,
  GitFork,
  CornerDownRight,
  LayoutPanelLeft,
  Milestone,
  Blend,
  Camera,
  PenTool,
  Share2,
  Brain,
  LayoutGrid,
  Grid2x2,
  Columns3,
  Layers,
  Timer,
  Compass,
  Cpu,
  NotebookPen,
  FileCode2,
  FileText,
  FileType,
  ScanText,
  Type,
  Stethoscope,
};

const BASE = "https://omnicanvasnotes.com";

export function ToolsIndex() {
  useMetaTags({
    title: "Free Tools for Thinking, Notes & Productivity | OmniCanvas",
    description:
      "Free online tools: whiteboard, mind map generator, brain dump organizer, Eisenhower matrix, SWOT maker, Cornell notes, flashcards and more. No signup.",
    url: `${BASE}/tools`,
  });

  return (
    <main className="bg-white pt-16">
      {/* JSON-LD: ItemList of tools */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "OmniCanvas Free Tools",
            itemListElement: tools.map((t, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: t.name,
              url: `${BASE}/tools/${t.slug}`,
            })),
          }),
        }}
      />

      {/* Hero */}
      <header className="max-w-5xl mx-auto px-6 pt-12 pb-10 text-center">
        <span className="inline-block text-xs font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 rounded-full px-3 py-1 mb-4">
          Free Tools
        </span>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-5">
          Free tools for thinking better
        </h1>
        <p className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto">
          A growing collection of free, no-signup tools for notes, planning, studying, and
          brainstorming. Each one runs right in your browser — and pairs perfectly with your
          OmniCanvas second brain.
        </p>
      </header>

      {/* Tool grid */}
      <section className="max-w-5xl mx-auto px-6 pb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {tools.map((t) => {
            const Icon = ICONS[t.icon] ?? PenTool;
            return (
              <a
                key={t.slug}
                href={`/tools/${t.slug}`}
                className="group flex flex-col rounded-2xl border border-slate-200 p-6 hover:border-indigo-300 hover:shadow-lg transition-all"
              >
                <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                  <Icon size={20} />
                </div>
                <h2 className="font-bold text-slate-900 mb-1.5 group-hover:text-indigo-700 transition-colors">
                  {t.name}
                </h2>
                <p className="text-sm text-slate-500 leading-relaxed flex-1">{t.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600">
                  Open tool
                  <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </a>
            );
          })}
        </div>
      </section>

      {/* Editorial */}
      <section className="max-w-3xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Why we build free tools</h2>
        <p className="text-slate-600 leading-relaxed mb-4">
          The best way to understand a thinking tool is to use one. Every tool here solves a
          real problem on its own — map an idea, prioritize a day, study for an exam, clear a
          cluttered mind — with no account and nothing to install.
        </p>
        <p className="text-slate-600 leading-relaxed mb-4">
          They also share a philosophy with OmniCanvas: your thinking should be visual,
          connected, and yours. When a quick tool isn't enough — when you want your work synced
          across devices, organized into folders, linked together, and waiting for you next
          time — that's what OmniCanvas, the infinite spatial second brain, is for.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-3xl px-8 py-14 md:px-12 text-center shadow-xl shadow-indigo-100">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
            Put it all on one infinite canvas
          </h2>
          <p className="text-indigo-100 max-w-xl mx-auto mb-7">
            Capture everything, link anything, and visualize your thoughts on a canvas that never
            ends. Free to start — no credit card.
          </p>
          <a
            href={withToolAttribution("https://app.omnicanvasnotes.com/?mode=try&action=new-note", "tools-index", "index_cta")}
            className="group inline-flex items-center gap-2.5 bg-white text-indigo-700 font-bold px-8 py-3.5 rounded-full shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
          >
            Start a canvas
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </section>
    </main>
  );
}
